import { useCallback, useMemo } from 'react';
import { useI18n } from '../i18n';

/**
 * Hook for locale-aware formatting of dates, numbers, and currencies
 * Uses the current i18n language for proper localization
 */
export const useLocaleFormatters = () => {
  const { language } = useI18n();

  // Map our language codes to proper BCP 47 locale codes
  const localeCode = useMemo(() => {
    const localeMap = {
      'en-US': 'en-US',
      'en-GB': 'en-GB',
      'af': 'af-ZA',
      'ar': 'ar-SA',
      'da': 'da-DK',
      'de': 'de-DE',
      'el': 'el-GR',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'hi': 'hi-IN',
      'id': 'id-ID',
      'it': 'it-IT',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
      'ms': 'ms-MY',
      'nl': 'nl-NL',
      'pl': 'pl-PL',
      'pt-BR': 'pt-BR',
      'pt-PT': 'pt-PT',
      'ro': 'ro-RO',
      'ru': 'ru-RU',
      'sk': 'sk-SK',
      'sv': 'sv-SE',
      'tr': 'tr-TR',
      'vi': 'vi-VN',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW'
    };
    return localeMap[language] || 'en-US';
  }, [language]);

  /**
   * Format a date according to the current locale
   * @param {Date|string|number} date - The date to format
   * @param {object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted date string
   */
  const formatDate = useCallback((date, options = {}) => {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };

    try {
      return new Intl.DateTimeFormat(localeCode, defaultOptions).format(dateObj);
    } catch {
      return dateObj.toLocaleDateString();
    }
  }, [localeCode]);

  /**
   * Format a date with time according to the current locale
   * @param {Date|string|number} date - The date to format
   * @param {object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted date and time string
   */
  const formatDateTime = useCallback((date, options = {}) => {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };

    try {
      return new Intl.DateTimeFormat(localeCode, defaultOptions).format(dateObj);
    } catch {
      return dateObj.toLocaleString();
    }
  }, [localeCode]);

  /**
   * Format a relative time (e.g., "2 days ago", "in 3 hours")
   * @param {Date|string|number} date - The date to compare
   * @returns {string} Relative time string
   */
  const formatRelativeTime = useCallback((date) => {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    const now = new Date();
    const diffInSeconds = Math.floor((dateObj - now) / 1000);
    const absSeconds = Math.abs(diffInSeconds);

    const units = [
      { unit: 'year', seconds: 31536000 },
      { unit: 'month', seconds: 2592000 },
      { unit: 'week', seconds: 604800 },
      { unit: 'day', seconds: 86400 },
      { unit: 'hour', seconds: 3600 },
      { unit: 'minute', seconds: 60 },
      { unit: 'second', seconds: 1 }
    ];

    for (const { unit, seconds } of units) {
      if (absSeconds >= seconds) {
        const value = Math.floor(diffInSeconds / seconds);
        try {
          return new Intl.RelativeTimeFormat(localeCode, { numeric: 'auto' }).format(value, unit);
        } catch {
          return `${Math.abs(value)} ${unit}${Math.abs(value) !== 1 ? 's' : ''} ${value < 0 ? 'ago' : 'from now'}`;
        }
      }
    }

    return 'just now';
  }, [localeCode]);

  /**
   * Format a number according to the current locale
   * @param {number} value - The number to format
   * @param {object} options - Intl.NumberFormat options
   * @returns {string} Formatted number string
   */
  const formatNumber = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '';
    
    try {
      return new Intl.NumberFormat(localeCode, options).format(value);
    } catch {
      return value.toString();
    }
  }, [localeCode]);

  /**
   * Format a currency value according to the current locale
   * @param {number} value - The amount to format
   * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
   * @param {object} options - Additional Intl.NumberFormat options
   * @returns {string} Formatted currency string
   */
  const formatCurrency = useCallback((value, currency = 'USD', options = {}) => {
    if (value === null || value === undefined) return '';
    
    const defaultOptions = {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options
    };

    try {
      return new Intl.NumberFormat(localeCode, defaultOptions).format(value);
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  }, [localeCode]);

  /**
   * Format a compact currency value (e.g., $1.2K, $3.5M)
   * @param {number} value - The amount to format
   * @param {string} currency - The currency code
   * @returns {string} Compact formatted currency string
   */
  const formatCompactCurrency = useCallback((value, currency = 'USD') => {
    if (value === null || value === undefined) return '';
    
    try {
      return new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(value);
    } catch {
      if (value >= 1000000) return `${currency} ${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${currency} ${(value / 1000).toFixed(1)}K`;
      return `${currency} ${value.toFixed(2)}`;
    }
  }, [localeCode]);

  /**
   * Format a percentage value
   * @param {number} value - The percentage value (0.15 for 15%)
   * @param {object} options - Intl.NumberFormat options
   * @returns {string} Formatted percentage string
   */
  const formatPercent = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '';
    
    const defaultOptions = {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options
    };

    try {
      return new Intl.NumberFormat(localeCode, defaultOptions).format(value);
    } catch {
      return `${(value * 100).toFixed(2)}%`;
    }
  }, [localeCode]);

  return {
    formatDate,
    formatDateTime,
    formatRelativeTime,
    formatNumber,
    formatCurrency,
    formatCompactCurrency,
    formatPercent,
    locale: localeCode
  };
};

export default useLocaleFormatters;
